import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, RefreshCw, Download } from "lucide-react";

const GRADE_COLOR = {
  P: "badge-present font-semibold",
  A: "badge-absent font-semibold",
  HD: "badge-halfday font-semibold",
  WO: "badge-weekoff",
  H: "badge-holiday font-semibold",
};

const fmtMin = (m) => {
  if (!m) return "—";
  const sign = m < 0 ? "-" : "";
  const abs = Math.abs(m);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
};

const currentMonthIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function BiometricAttendanceGrid() {
  const [month, setMonth] = useState(currentMonthIso());
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ days: [], employees: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/biometric/attendance-grid", {
        params: { month, q: search || undefined },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load biometric grid");
      setData({ days: [], employees: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const filteredEmps = useMemo(() => {
    if (!search) return data.employees || [];
    const q = search.toLowerCase();
    return (data.employees || []).filter(e =>
      (e.name || "").toLowerCase().includes(q) ||
      (e.employee_code || "").toLowerCase().includes(q) ||
      (e.punch_id || "").toLowerCase().includes(q)
    );
  }, [data.employees, search]);

  const exportCsv = () => {
    if (!filteredEmps.length) return toast.error("Nothing to export");
    const days = data.days || [];
    const header = ["Employee", "Code", "Bio ID", "Shift", "Field",
                    ...days.map(d => `${d.day} ${d.weekday}`)];
    const rows = [header];
    const fields = [
      { key: "first_in", label: "FIRST IN" },
      { key: "last_out", label: "LAST OUT" },
      { key: "total_hrs_raw", label: "TOTAL HRS" },
      { key: "grade", label: "GRADE" },
    ];
    filteredEmps.forEach(emp => {
      fields.forEach((f, idx) => {
        const row = [
          idx === 0 ? emp.name : "",
          idx === 0 ? emp.employee_code : "",
          idx === 0 ? emp.punch_id : "",
          idx === 0 ? `${emp.shift_hours}h` : "",
          f.label,
        ];
        days.forEach(d => {
          const day = emp.days[d.day];
          if (!day) return row.push("");
          if (f.key === "first_in") row.push(day.first_in || "");
          else if (f.key === "last_out") row.push(day.last_out || "");
          else if (f.key === "total_hrs_raw") row.push(day.total_hrs_raw || "");
          else if (f.key === "grade") row.push(day.grade || "");
        });
        rows.push(row);
      });
      // Monthly summary row per employee
      const sm = emp.summary || {};
      rows.push([
        "", "", "", "", "TOTAL",
        `Present:${sm.present_days || 0}`,
        `Absent:${sm.absent_days || 0}`,
        `WO:${sm.weekly_off_days || 0}`,
        `HD:${sm.half_days || 0}`,
        `Worked:${sm.total_worked_hours || 0}h`,
        `OT:${sm.total_ot_hours || 0}h`,
        `Punches:${sm.total_punches || 0}`,
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `biometric_attendance_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="biometric-attendance-grid" className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-md p-3">
        <Input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-9 w-40"
          data-testid="bio-grid-month"
        />
        <div className="flex items-center gap-1 flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, code, or biometric ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9"
            data-testid="bio-grid-search"
          />
        </div>
        <span className="text-xs text-slate-500">
          {filteredEmps.length} of {data.employees?.length || 0} employees mapped
        </span>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                data-testid="bio-grid-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv}
                data-testid="bio-grid-export">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {!loading && filteredEmps.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-800">
          No mapped employees with biometric data for <b>{month}</b>.
          Map employees first via the "Import from device (CSV)" tab.
        </div>
      )}

      {/* Grid */}
      {filteredEmps.length > 0 && (
        <div className="bg-white border border-border rounded-md overflow-hidden">
          <div className="overflow-x-auto" style={{ maxHeight: "75vh" }}>
            <table className="text-xs border-collapse" data-testid="bio-grid-table">
              <thead className="bg-[#FAF7F7] sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-left sticky left-0 bg-[#FAF7F7] text-[#6B5B5C] z-20 w-48">Employee</th>
                  <th className="px-2 py-2 text-left bg-[#FAF7F7] text-[#6B5B5C]">Field</th>
                  {(data.days || []).map(d => (
                    <th
                      key={d.day}
                      className={`px-1 py-2 text-center w-14 border-l border-border text-[#6B5B5C] ${
                        d.is_sunday ? "bg-amber-100" : "bg-[#FAF7F7]"
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <span className="text-sm">{d.day}</span>
                        <span className={`text-[9px] uppercase tracking-wider ${
                          d.is_sunday ? "text-amber-800 font-semibold" : "text-[#6B5B5C]"
                        }`}>{d.weekday}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              {filteredEmps.map(emp => {
                const rows = [
                  { key: "first_in", label: "First IN" },
                  { key: "last_out", label: "Last Out" },
                  { key: "total_hrs_raw", label: "Total Hrs" },
                  { key: "shift", label: "Shift" },
                  { key: "ot_minutes", label: "OT" },
                  { key: "grade", label: "Grade" },
                ];
                const sm = emp.summary || {};
                return (
                  <tbody key={emp.employee_id} className="border-t-2 border-slate-300">
                    {rows.map((r, ri) => (
                      <tr key={`${emp.employee_id}-${r.key}`} className="border-b border-border hover:bg-[#FAF7F7]">
                        {ri === 0 && (
                          <td rowSpan={7}
                              className="px-2 py-1 sticky left-0 bg-white border-r border-border align-top z-10 w-48">
                            <div className="text-xs font-semibold text-foreground truncate">{emp.name}</div>
                            <div className="text-[10px] text-muted-foreground font-mono">
                              {emp.employee_code}
                            </div>
                            <div className="text-[10px] text-muted-foreground font-mono flex flex-col">
                              <span>Bio: <b className="text-emerald-700">{emp.biometric_id || "—"}</b></span>
                              <span>Pay: {emp.paytime_punch_id || "—"}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              {emp.category || ""} · {emp.shift_hours}h · P:{emp.present_days}
                            </div>
                            <div className="mt-2 pt-2 border-t border-border space-y-0.5">
                              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Month Summary</div>
                              <div className="text-[10px] text-emerald-700 font-mono">Present: <b>{sm.present_days || 0}</b></div>
                              <div className="text-[10px] text-red-700 font-mono">Absent: <b>{sm.absent_days || 0}</b></div>
                              <div className="text-[10px] text-[#5F5E5A] font-mono">WO: <b>{sm.weekly_off_days || 0}</b>{sm.half_days ? <> · HD: <b>{sm.half_days}</b></> : null}</div>
                              <div className="text-[10px] text-foreground font-mono">Worked: <b>{sm.total_worked_hours || 0}h</b></div>
                              <div className="text-[10px] text-amber-700 font-mono">OT: <b>{sm.total_ot_hours || 0}h</b></div>
                            </div>
                          </td>
                        )}
                        <td className="px-2 py-1 bg-[#FAF7F7] text-[10px] text-[#6B5B5C] font-medium uppercase tracking-wider whitespace-nowrap">
                          {r.label}
                        </td>
                        {(data.days || []).map(d => {
                          const day = emp.days[d.day];
                          const sundayBg = d.is_sunday ? "bg-amber-50/60" : "";
                          if (!day) return <td key={d.day} className={`px-1 py-1 text-center border-l border-border ${sundayBg}`}>—</td>;
                          let display = "—";
                          let cls = "";
                          if (r.key === "first_in") display = day.first_in || "00:00";
                          else if (r.key === "last_out") display = day.last_out || "00:00";
                          else if (r.key === "total_hrs_raw") display = day.total_hrs_raw || "00:00";
                          else if (r.key === "shift") display = `${day.shift_hours || ""}`;
                          else if (r.key === "ot_minutes") {
                            display = day.ot_minutes ? fmtMin(day.ot_minutes) : "—";
                            if (day.ot_minutes > 0) cls = "bg-amber-50 text-amber-800 font-medium";
                            else if (day.ot_minutes < 0) cls = "bg-red-50 text-red-700 font-medium";
                          } else if (r.key === "grade") {
                            display = day.grade || "—";
                            cls = day.grade ? (GRADE_COLOR[day.grade] || "") : "";
                          }
                          return (
                            <td key={d.day}
                                className={`px-1 py-1 text-center border-l border-border ${sundayBg} ${cls}`}
                                title={day.punch_count ? `${day.punch_count} punch(es)` : "No punches"}
                                data-testid={`bio-cell-${emp.employee_id}-${d.day}-${r.key}`}>
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {/* Monthly summary row — totals across all days */}
                    <tr className="bg-red-50/70 border-t border-red-200" data-testid={`bio-summary-row-${emp.employee_id}`}>
                      <td className="px-2 py-1 bg-red-100 text-[10px] text-red-900 font-bold uppercase tracking-wider whitespace-nowrap">
                        Total
                      </td>
                      <td colSpan={(data.days || []).length}
                          className="px-3 py-1.5 text-[11px] text-foreground">
                        <span className="inline-flex items-center gap-4">
                          <span><b className="text-emerald-700 font-mono">{sm.present_days || 0}</b> Present</span>
                          <span><b className="text-red-700 font-mono">{sm.absent_days || 0}</b> Absent</span>
                          <span><b className="text-[#5F5E5A] font-mono">{sm.weekly_off_days || 0}</b> WO</span>
                          {sm.half_days > 0 && <span><b className="text-amber-700 font-mono">{sm.half_days}</b> Half-day</span>}
                          <span className="text-muted-foreground">|</span>
                          <span>Worked <b className="font-mono">{sm.total_worked_hours || 0}h</b> · Expected <b className="font-mono">{sm.expected_shift_hours_for_present || 0}h</b></span>
                          <span className="text-amber-700">OT <b className="font-mono">{sm.total_ot_hours || 0}h</b></span>
                          <span className="text-muted-foreground text-[10px]">({sm.total_punches || 0} punches)</span>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                );
              })}
            </table>
          </div>
          <div className="px-4 py-2 border-t border-border bg-[#FAF7F7] text-[10px] text-[#6B5B5C] flex flex-wrap gap-4">
            <span><span className="inline-block w-3 h-3 bg-[#E3F0E8] align-middle mr-1"></span>Present</span>
            <span><span className="inline-block w-3 h-3 bg-[#FBE9EA] align-middle mr-1"></span>Absent</span>
            <span><span className="inline-block w-3 h-3 bg-[#FFF3D6] align-middle mr-1"></span>Half day / OT</span>
            <span><span className="inline-block w-3 h-3 bg-[#F1EFE8] align-middle mr-1"></span>Weekly off</span>
            <span className="ml-auto italic">Derived live from biometric_punches — payroll source untouched</span>
          </div>
        </div>
      )}
    </div>
  );
}
